import React, { useEffect, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  fetchCategoryListing,
  fetchNextPage,
  resetFilters,
  changeFilterApiStatus,
} from '@nykaa/listing-shared/category/store/action';
import {
  getUrlStringFromSelectedFilter,
  getApiRequestObject,
  createActiveFilterObjectFromURL,
} from '@nykaa/listing-shared/utils/filter';
import { useDispatch, useSelector } from 'react-redux';
// import { fetchPlpWidgets } from '@nykaa/listing-shared/category/store/action';
import ArtWidget from '@nykaa/art-widget-desktop';
import styled from 'App/styled';
import Error from 'shared/components/Error';
import Widgets from 'components/Widgets';
import MetaInfo from 'components/MetaInfo';
import ListingComponent from 'components/ListingPage';
import BreadCrumb from 'components/Breadcrumb';
import { State } from 'store/types';
import { usePrevious } from 'hooks/prevState';
import Title from './Title';
import NoResults from './NoResults';
import {
  fetchFooterCategories,
  fetchCategoryPriceListing,
  fetchAboutUs,
  fetchSeoFooterCategories,
} from 'store/footer/action';
import { addParamInURL } from 'helpers/utils/url';
import { titleId } from 'components/ListingPage/Scroll/constants';
//import { getStaticBreadCrumb } from 'constants/breadcrumb';
import {
  artImagePrefix as defaultArtImagePrefix,
  defaultArtVideoImagePrefix,
  deviceTypes,
} from 'shared/constants';
import WidgetCarousal from './Widgets';
import PageTypes from 'constants/pageTypes';
import { getAbsoluteUrl } from '@nykaa/utils/network/urls';
import getDynamicAlgoParams from 'helpers/dynamicAlgoParams';
import { QUERY_PARAM_EXP_DWEB } from 'constants/experiments';
import { useIsTagsV2Enabled } from 'hooks/Product/tags';
import { useAggregatorFooter } from 'shared/hooks/category';
import { fetchBreadCrumbs } from 'store/app/actions';
import Footer from 'components/Footer/footer';

const Wrapper = styled.div`
  width: 100%;
  background: #f3f3f3;
`;
const Container = styled.div`
  max-width: 1200px;
  margin: 0px auto;
`;

interface Props {
  match: any;
  priveBand: string;
  fetchFooterDataOnClient: boolean;
}

function Layout({ match, priveBand, fetchFooterDataOnClient }: Props) {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const ref = useRef(false);
  const rootState = useSelector((state: State) => state);
  const categoryListing = useSelector((state: State) => state.categoryListing);
  const remoteConfig = useSelector((state: State) => state.remoteConfig);
  const isPro = useSelector(({ auth }: State) => auth.user.isPro);
  const groupId = useSelector(({ auth }: State) => auth.user.groupId);
  // const showBreadcrumb = useSelector(({ appReducer }: State) => appReducer?.configFlags?.showBreadcrumb);
  const isSeoFooterEnabled = remoteConfig?.switches?.seoFooterCategoryEnable;
  const isSeoDataNeeded = true;
  const listingData = categoryListing?.listingData;
  const isFilterChanged = categoryListing?.isFilterChanged || false;
  const isPageChanged = categoryListing?.isPageChanged || false;
  const widgets = categoryListing?.widgets || [];
  const remainingInventoryIds =
    categoryListing?.remaining_inventories_ids || [];
  const queryParams = categoryListing?.queryParamsObject || {};
  const activeFilters = categoryListing?.activeFilters;
  const serverError = categoryListing?.serverError || false;
  const {
    categoryName,
    totalFound = 0,
    filters,
    count = 0,
    listingType,
  } = listingData || {};
  const id = match?.params?.id || '';
  const search = location?.search;
  const listingPriceRevealEnabled =
    remoteConfig?.configs?.listingPriceReveal?.isActive;

  const metaTitle = listingData?.metaTitle;
  const metaDescription = listingData?.metaDescription;
  const metaKeywords = listingData?.metaKeywords;
  const canonical =
    listingData?.canonicalUrl ||
    getAbsoluteUrl(listingData?.canonical as string);
  const prevId = usePrevious(id);
  const isIdChange = prevId !== id;
  const artContent = listingData?.artContent;
  const artImage = listingData?.artImage;
  const artTitle = listingData?.artTitle;
  const artURL = listingData?.artURL;
  const artLinkText = listingData?.linkText;
  const artBannerVideo = listingData?.artBannerVideo;
  const artVideoImage = listingData?.artVideoImage;

  const artImagePrefix =
    useSelector(
      (state: State) => state.remoteConfig.configs?.artWidget?.imagePrefix
    ) || defaultArtImagePrefix;
  const artVideoImagePrefix =
    useSelector(
      (state: State) => state.remoteConfig.configs?.artWidget?.VideoImagePrefix
    ) || defaultArtVideoImagePrefix;
  const tagsOnListingEnabled = useSelector(
    (state: State) => state.remoteConfig.switches?.secondaryTagEnabledPLP
  );
  const tagsV2Enabled = useIsTagsV2Enabled();
  const { isAggregatorFooterEnabled } = useAggregatorFooter();
  const handleFilterAndSortReset = useCallback(() => {
    const filterParams = createActiveFilterObjectFromURL(filters, search);
    dispatch(resetFilters(filterParams));
  }, [dispatch, filters, search]);

  const fetchFunc = useCallback(
    (params) =>
      isPageChanged
        ? dispatch(fetchNextPage(params))
        : dispatch(fetchCategoryListing(params)),
    [dispatch, isPageChanged]
  );
  const getFilterParam = useCallback(() => {
    const urlString = getUrlStringFromSelectedFilter(activeFilters);
    const params = getApiRequestObject(urlString);
    return { urlString, params };
  }, [activeFilters]);

  const fetchData = useCallback(() => {
    const { urlString, params } = getFilterParam();
    const dynamicParams = getDynamicAlgoParams(rootState, QUERY_PARAM_EXP_DWEB);
    const parameters = {
      categoryId: id,
      isPro,
      customer_group_id: groupId,
      pageNumber: Number(activeFilters.page_no) || 1,
      concat: !isFilterChanged,
      platform: deviceTypes.D_SITE,
      listingPriceRevealEnabled,
      ...params,
      ...dynamicParams,
      priveBand,
    };
    fetchFunc(parameters)
      .then(() => {
        addParamInURL(history, location, urlString);
        if (isPageChanged) {
          const titleTop =
            (document.getElementById(titleId) as HTMLElement)?.offsetTop -
              100 || 0;
          window.scroll({
            top: titleTop,
            left: 0,
            behavior: 'smooth',
          });
        }
      })
      .catch(() => {
        handleFilterAndSortReset();
        changeFilterApiStatus(true)(dispatch);
      });
  }, [
    getFilterParam,
    rootState,
    id,
    isPro,
    groupId,
    activeFilters.page_no,
    isFilterChanged,
    listingPriceRevealEnabled,
    fetchFunc,
    history,
    location,
    isPageChanged,
    handleFilterAndSortReset,
    dispatch,
    priveBand,
  ]);
  useEffect(() => {
    if (isPageChanged || isFilterChanged) {
      fetchData();
    }
  }, [isPageChanged, isFilterChanged, fetchData]);

  // const fetchWidgets = useCallback(() => {
  //   if (id) {
  //     dispatch(fetchPlpWidgets(id, isPro, deviceTypes.D_SITE));
  //   }
  // }, [dispatch, id, isPro]);

  // useEffect(() => {
  //   fetchWidgets();
  // }, [fetchWidgets]);
  const fetchFooterData = useCallback(() => {
    // For aggregator flow, allow client footer APIs only when timeout fallback says so.
    if (isAggregatorFooterEnabled && !fetchFooterDataOnClient) {
      return;
    }

    if (!serverError && isSeoDataNeeded) {
      dispatch(fetchAboutUs({ category_id: id, domain: __PLATFORM__ }));
      if (isSeoFooterEnabled) {
        dispatch(
          fetchSeoFooterCategories({ cat_id: id, domain: __PLATFORM__ })
        );
      } else {
        dispatch(fetchCategoryPriceListing({ category_id: id }));
        dispatch(fetchFooterCategories({ id }));
      }
    }
  }, [
    isAggregatorFooterEnabled,
    fetchFooterDataOnClient,
    serverError,
    isSeoDataNeeded,
    dispatch,
    id,
    isSeoFooterEnabled,
  ]);

  useEffect(() => {
    if (ref.current && isIdChange) {
      handleFilterAndSortReset();
      fetchData();
      dispatch(fetchBreadCrumbs(id));
    }
    ref.current = true;
  }, [fetchData, handleFilterAndSortReset, isIdChange, dispatch, id]);

  useEffect(() => {
    fetchFooterData();
  }, [
    dispatch,
    fetchFooterData,
    id,
    isAggregatorFooterEnabled,
    fetchFooterDataOnClient,
  ]);

  if (serverError) {
    return <Error />;
  }
  // let breadcrumb;
  // if (!showBreadcrumb) {
  //   breadcrumb = getStaticBreadCrumb(categoryName);
  // }
  const artWidgetConfigs = {
    title: artTitle,
    content: artContent,
    link: artURL,
    linkText: artLinkText,
    image: `${artImagePrefix}/${artImage}`,
    videoImage: `${artVideoImagePrefix}/${artVideoImage}`,
    video: artBannerVideo,
  };
  const journeyData = {
    siteNavigation: 'shop',
    siteSubNavigation: `shop:${listingType}:${id}`,
  };
  return (
    <>
      <Wrapper>
        <Container>
          {isSeoDataNeeded && (
            <MetaInfo
              metaTitle={metaTitle}
              metaDescription={metaDescription}
              metaKeywords={metaKeywords}
              canonical={canonical}
              page={PageTypes.CATEGORY}
              id={id}
            />
          )}
          <BreadCrumb />
          {count > 0 && (
            <>
              <Title
                categoryName={categoryName}
                totalProducts={totalFound}
              />
              <Widgets
                widgets={widgets}
                categoryName={categoryName}
                remainingInventoryIds={remainingInventoryIds}
                queryParamsObject={queryParams}
                pageData={id}
              />
              <ArtWidget configs={artWidgetConfigs} />
              <WidgetCarousal />
              <Title title="All Products" />
              <ListingComponent
                journeyData={journeyData}
                tagsOnListingEnabled={tagsOnListingEnabled}
                tagsV2Enabled={tagsV2Enabled}
              />
            </>
          )}
          {count === 0 && <NoResults />}
        </Container>
      </Wrapper>
      {isAggregatorFooterEnabled && (
        <Footer shouldLazyLoadSubComponent={false} />
      )}
    </>
  );
}

export default Layout;
